import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import styles from './Atendimento.module.css';
import LoadingSpinner from '../components/LoadingSpinner';

const Atendimento = () => {
  const { consultaId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Detectar modo visualização (somente leitura)
  const modoVisualizacao = location.state?.modoVisualizacao || false;
  
  const [loading, setLoading] = useState(false);
  const [prontuarioData, setProntuarioData] = useState(null);
  const [evolucoes, setEvolucoes] = useState([]);
  const [showHistorico, setShowHistorico] = useState(false);
  
  // Modais de edição de dados fixos
  const [showModalAlergias, setShowModalAlergias] = useState(false);
  const [showModalMedicamentos, setShowModalMedicamentos] = useState(false);
  const [showModalHistoricoFamiliar, setShowModalHistoricoFamiliar] = useState(false);
  const [showModalCirurgias, setShowModalCirurgias] = useState(false);
  const [showModalHistoricoReprodutivo, setShowModalHistoricoReprodutivo] = useState(false);
  const [showModalObjetivo, setShowModalObjetivo] = useState(false);
  
  // Estados temporários para edição
  const [tempAlergias, setTempAlergias] = useState('');
  const [tempMedicamentos, setTempMedicamentos] = useState('');
  const [tempHistoricoFamiliar, setTempHistoricoFamiliar] = useState('');
  const [tempCirurgias, setTempCirurgias] = useState('');
  const [tempHistoricoReprodutivo, setTempHistoricoReprodutivo] = useState({
    ciclo_menstrual_regular: '',
    usa_anticoncepcional: '',
    dificuldade_engravidar: '',
    menopausa_status: '',
    sintomas_menopausa: '',
    alteracoes_sexuais_masculinas: '',
    historico_reprodutivo: ''
  });
  const [tempObjetivo, setTempObjetivo] = useState('');
  
  // Formulário de nova evolução
  const [novaEvolucao, setNovaEvolucao] = useState({
    // Sinais Vitais
    peso: '',
    altura: '',
    pressao_arterial: '',
    frequencia_cardiaca: '',
    temperatura: '',
    saturacao_oxigenio: '',
    glicemia: '',
    circunferencia_abdominal: '',
    
    // Sintomas e Estilo de Vida
    sintomas_atuais: '',
    medicamentos_atuais_consulta: '',
    estilo_vida_atual: '',
    
    // Exames Laboratoriais
    exames_lab_tsh_t4_t3: '',
    exames_lab_hemograma: '',
    exames_lab_glicemia_hba1c: '',
    exames_lab_insulina_homa: '',
    exames_lab_vitamina_d: '',
    exames_lab_cortisol: '',
    exames_lab_prolactina: '',
    exames_lab_perfil_lipidico: '',
    exames_lab_funcao_hepatica: '',
    exames_lab_funcao_renal: '',
    exames_lab_ultrassom: '',
    exames_lab_outros: '',
    
    // Avaliação
    queixa: '',
    exame_fisico: '',
    hipotese_diagnostica: '',
    conduta: '',
    plano_terapeutico: '',
    observacoes: ''
  });

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    // Se veio do estado de navegação, usar os dados
    if (location.state?.prontuarioData) {
      setProntuarioData(location.state.prontuarioData);
    } else {
      // Caso contrário, carregar os dados
      carregarDadosAtendimento();
    }
  }, [consultaId]);

  const carregarDadosAtendimento = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`/agendamentos/${consultaId}/iniciar-atendimento`);
      setProntuarioData(response.data.data || response.data);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError(err.response?.data?.message || 'Erro ao carregar dados do atendimento');
    } finally {
      setLoading(false);
    }
  };

  const carregarHistorico = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/agendamentos/${consultaId}/historico-evolucoes`);
      setEvolucoes(response.data.data || response.data.evolucoes || []);
      setShowHistorico(true);
    } catch (err) {
      console.error('Erro ao carregar histórico:', err);
      setError('Erro ao carregar histórico de evoluções');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNovaEvolucao(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calcularIMC = () => {
    const peso = parseFloat(novaEvolucao.peso);
    const altura = parseFloat(novaEvolucao.altura);
    
    if (peso && altura && altura > 0) {
      const imc = peso / (altura * altura);
      return imc.toFixed(2);
    }
    return null;
  };

  const registrarEvolucao = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const evolucaoData = { ...novaEvolucao };
      
      // Remover campos vazios
      Object.keys(evolucaoData).forEach(key => {
        if (evolucaoData[key] === '') {
          delete evolucaoData[key];
        }
      });

      await axios.post(`/agendamentos/${consultaId}/registrar-evolucao`, evolucaoData);
      
      setSuccess('✅ Evolução registrada com sucesso!');
      
      // Limpar formulário
      setNovaEvolucao({
        peso: '',
        altura: '',
        pressao_arterial: '',
        frequencia_cardiaca: '',
        temperatura: '',
        saturacao_oxigenio: '',
        queixa: '',
        exame_fisico: '',
        hipotese_diagnostica: '',
        conduta: '',
        observacoes: ''
      });

      // Recarregar dados
      setTimeout(() => {
        carregarDadosAtendimento();
        setSuccess('');
      }, 2000);

    } catch (err) {
      console.error('Erro ao registrar evolução:', err);
      setError(err.response?.data?.message || 'Erro ao registrar evolução');
    } finally {
      setLoading(false);
    }
  };

  const finalizarAtendimento = async () => {
    if (!window.confirm('Deseja finalizar o atendimento?')) {
      return;
    }

    try {
      setLoading(true);
      await axios.post(`/agendamentos/${consultaId}/finalizar-atendimento`);
      
      alert('✅ Atendimento finalizado com sucesso!');
      navigate('/agendamentos');
    } catch (err) {
      console.error('Erro ao finalizar atendimento:', err);
      setError(err.response?.data?.message || 'Erro ao finalizar atendimento');
      setLoading(false);
    }
  };

  const formatarData = (dataStr) => {
    const data = new Date(dataStr);
    return data.toLocaleDateString('pt-BR');
  };

  const formatarDataHora = (dataStr) => {
    const data = new Date(dataStr);
    return data.toLocaleString('pt-BR');
  };

  // Funções para abrir modais de edição
  const abrirModalAlergias = () => {
    if (!prontuarioData?.prontuario) return;
    
    // Combinar todos os tipos de alergia em um único texto
    const { alergias_medicamentos, alergias_alimentos, outras_alergias } = prontuarioData.prontuario;
    let alergiasCombinadas = '';
    
    if (alergias_medicamentos) alergiasCombinadas += `Medicamentos:\n${alergias_medicamentos}\n\n`;
    if (alergias_alimentos) alergiasCombinadas += `Alimentos:\n${alergias_alimentos}\n\n`;
    if (outras_alergias) alergiasCombinadas += outras_alergias;
    
    setTempAlergias(alergiasCombinadas.trim());
    setShowModalAlergias(true);
  };

  const abrirModalMedicamentos = () => {
    if (!prontuarioData?.prontuario) return;
    setTempMedicamentos(prontuarioData.prontuario.medicamentos_uso_continuo || '');
    setShowModalMedicamentos(true);
  };

  const abrirModalHistoricoFamiliar = () => {
    if (!prontuarioData?.prontuario) return;
    setTempHistoricoFamiliar(prontuarioData.prontuario.historia_familiar || '');
    setShowModalHistoricoFamiliar(true);
  };

  const abrirModalCirurgias = () => {
    if (!prontuarioData?.prontuario) return;
    setTempCirurgias(prontuarioData.prontuario.cirurgias_previas || '');
    setShowModalCirurgias(true);
  };

  const abrirModalHistoricoReprodutivo = () => {
    if (!prontuarioData?.prontuario) return;
    setTempHistoricoReprodutivo({
      ciclo_menstrual_regular: prontuarioData.prontuario.ciclo_menstrual_regular || 'nao_se_aplica',
      usa_anticoncepcional: prontuarioData.prontuario.usa_anticoncepcional || '',
      dificuldade_engravidar: prontuarioData.prontuario.dificuldade_engravidar || 'nao_se_aplica',
      menopausa_status: prontuarioData.prontuario.menopausa_status || 'nao_se_aplica',
      sintomas_menopausa: prontuarioData.prontuario.sintomas_menopausa || '',
      alteracoes_sexuais_masculinas: prontuarioData.prontuario.alteracoes_sexuais_masculinas || '',
      historico_reprodutivo: prontuarioData.prontuario.historico_reprodutivo || ''
    });
    setShowModalHistoricoReprodutivo(true);
  };

  const abrirModalObjetivo = () => {
    if (!prontuarioData?.prontuario) return;
    setTempObjetivo(prontuarioData.prontuario.objetivo_paciente || '');
    setShowModalObjetivo(true);
  };

  // Função para atualizar dados do prontuário
  const atualizarDadosProntuario = async (campo, valor) => {
    if (!prontuarioData?.prontuario?.id) {
      setError('Erro: Prontuário não encontrado');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const prontuarioId = prontuarioData.prontuario.id;
      
      const response = await axios.put(`/prontuarios/${prontuarioId}`, {
        [campo]: valor
      });

      // Atualizar estado local com os dados retornados do backend
      if (response.data.success && response.data.data) {
        setProntuarioData(prev => ({
          ...prev,
          prontuario: {
            ...prev.prontuario,
            ...response.data.data
          }
        }));
      } else {
        // Fallback: atualizar apenas o campo modificado
        setProntuarioData(prev => ({
          ...prev,
          prontuario: {
            ...prev.prontuario,
            [campo]: valor
          }
        }));
      }

      setSuccess(`✅ Dados atualizados com sucesso!`);
      setTimeout(() => setSuccess(''), 3000);

    } catch (err) {
      // Log mínimo para não vazar objetos grandes no console
      console.error('Erro ao atualizar prontuário:', err?.message || err);
      setError(err.response?.data?.message || 'Erro ao atualizar dados');
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const salvarAlergias = async () => {
    await atualizarDadosProntuario('outras_alergias', tempAlergias);
    setShowModalAlergias(false);
  };

  const salvarMedicamentos = async () => {
    await atualizarDadosProntuario('medicamentos_uso_continuo', tempMedicamentos);
    setShowModalMedicamentos(false);
  };

  const salvarHistoricoFamiliar = async () => {
    await atualizarDadosProntuario('historia_familiar', tempHistoricoFamiliar);
    setShowModalHistoricoFamiliar(false);
  };

  const salvarCirurgias = async () => {
    await atualizarDadosProntuario('cirurgias_previas', tempCirurgias);
    setShowModalCirurgias(false);
  };

  const salvarHistoricoReprodutivo = async () => {
    // Atualizar cada campo individualmente
    const promises = Object.keys(tempHistoricoReprodutivo).map(campo => 
      atualizarDadosProntuario(campo, tempHistoricoReprodutivo[campo])
    );
    
    await Promise.all(promises);
    setShowModalHistoricoReprodutivo(false);
  };

  const salvarObjetivo = async () => {
    await atualizarDadosProntuario('objetivo_paciente', tempObjetivo);
    setShowModalObjetivo(false);
  };

  if (loading && !prontuarioData) {
    return <LoadingSpinner />;
  }

  if (!prontuarioData || !prontuarioData.paciente || !prontuarioData.prontuario) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          ⚠️ Dados do atendimento não encontrados
        </div>
      </div>
    );
  }

  const { prontuario, paciente, ultima_evolucao } = prontuarioData;

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <button 
          className={styles.btnVoltar}
          onClick={() => navigate('/agendamentos')}
        >
          ← Voltar
        </button>
        <h1>{modoVisualizacao ? 'Visualização de Prontuário' : 'Atendimento em Andamento'}</h1>
        {!modoVisualizacao && (
          <button 
            className={styles.btnFinalizar}
            onClick={finalizarAtendimento}
            disabled={loading}
          >
            ✓ Finalizar Atendimento
          </button>
        )}
      </div>

      {error && <div className={styles.error}>⚠️ {error}</div>}
      {success && <div className={styles.success}>{success}</div>}

      {/* Banner Modo Visualização */}
      {modoVisualizacao && (
        <div className={styles.modoVisualizacao}>
          👁️ MODO VISUALIZAÇÃO - Consulta Concluída (Somente Leitura)
        </div>
      )}

      {/* Informações do Paciente */}
      <div className={styles.pacienteBox}>
        <div className={styles.pacienteHeader}>
          <h2>👤 {paciente.nome_completo}</h2>
          <span className={styles.prontuarioNumero}>📋 {prontuario.numero_prontuario}</span>
        </div>
        <div className={styles.pacienteInfo}>
          <div><strong>CPF:</strong> {paciente.cpf}</div>
          <div><strong>Data Nascimento:</strong> {formatarData(paciente.data_nascimento)}</div>
          {paciente.telefone && <div><strong>Telefone:</strong> {paciente.telefone}</div>}
          {paciente.email && <div><strong>Email:</strong> {paciente.email}</div>}
        </div>
      </div>

      {/* Layout de 2 Colunas */}
      <div className={`${styles.layoutColunas} ${modoVisualizacao ? styles.layoutVisualizacao : ''}`}>
        
        {/* COLUNA ESQUERDA - Dados Fixos */}
        <div className={styles.colunaEsquerda}>
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h3>📌 Dados do Prontuário</h3>
              <button 
                className={styles.btnHistorico}
                onClick={carregarHistorico}
                disabled={loading}
              >
                📜 Ver Histórico
              </button>
            </div>

            {/* Alergias */}
            <div className={styles.subsection}>
              <div className={styles.subsectionHeader}>
                <h4>⚠️ Alergias</h4>
                {!modoVisualizacao && (
                  <button 
                    className={styles.btnEditar}
                    onClick={abrirModalAlergias}
                    title="Editar Alergias"
                  >
                    ✏️ Editar
                  </button>
                )}
              </div>
              {(prontuario.alergias_medicamentos || prontuario.alergias_alimentos || prontuario.outras_alergias) ? (
                <div className={styles.alertBox}>
                  {prontuario.alergias_medicamentos && <div><strong>Medicamentos:</strong> {prontuario.alergias_medicamentos}</div>}
                  {prontuario.alergias_alimentos && <div><strong>Alimentos:</strong> {prontuario.alergias_alimentos}</div>}
                  {prontuario.outras_alergias && <div>{prontuario.outras_alergias}</div>}
                </div>
              ) : (
                <p className={styles.semDados}>Nenhuma alergia registrada</p>
              )}
            </div>

            {/* Medicamentos Contínuos */}
            <div className={styles.subsection}>
              <div className={styles.subsectionHeader}>
                <h4>💊 Medicamentos de Uso Contínuo</h4>
                {!modoVisualizacao && (
                  <button 
                    className={styles.btnEditar}
                    onClick={abrirModalMedicamentos}
                    title="Editar Medicamentos"
                  >
                    ✏️ Editar
                  </button>
                )}
              </div>
              {prontuario.medicamentos_uso_continuo ? (
                <div className={styles.infoBox}>{prontuario.medicamentos_uso_continuo}</div>
              ) : (
                <p className={styles.semDados}>Nenhum medicamento contínuo</p>
              )}
            </div>

            {/* Histórico Familiar */}
            <div className={styles.subsection}>
              <div className={styles.subsectionHeader}>
                <h4>👨‍👩‍👧‍👦 Histórico Familiar</h4>
                {!modoVisualizacao && (
                  <button 
                    className={styles.btnEditar}
                    onClick={abrirModalHistoricoFamiliar}
                    title="Editar Histórico Familiar"
                  >
                    ✏️ Editar
                  </button>
                )}
              </div>
              {prontuario.historia_familiar ? (
                <div className={styles.infoBox}>{prontuario.historia_familiar}</div>
              ) : (
                <p className={styles.semDados}>Sem histórico familiar registrado</p>
              )}
            </div>

            {/* Cirurgias Prévias */}
            <div className={styles.subsection}>
              <div className={styles.subsectionHeader}>
                <h4>🏥 Cirurgias Prévias</h4>
                {!modoVisualizacao && (
                  <button 
                    className={styles.btnEditar}
                    onClick={abrirModalCirurgias}
                    title="Editar Cirurgias Prévias"
                  >
                    ✏️ Editar
                  </button>
                )}
              </div>
              {prontuario.cirurgias_previas ? (
                <div className={styles.infoBox}>{prontuario.cirurgias_previas}</div>
              ) : (
                <p className={styles.semDados}>Nenhuma cirurgia prévia</p>
              )}
            </div>

            {/* Histórico Reprodutivo */}
            <div className={styles.subsection}>
              <div className={styles.subsectionHeader}>
                <h4>🔬 Histórico Reprodutivo/Hormonal</h4>
                {!modoVisualizacao && (
                  <button 
                    className={styles.btnEditar}
                    onClick={abrirModalHistoricoReprodutivo}
                    title="Editar Histórico Reprodutivo"
                  >
                    ✏️ Editar
                  </button>
                )}
              </div>
              <div className={styles.infoBox}>
                {prontuario.ciclo_menstrual_regular && prontuario.ciclo_menstrual_regular !== 'nao_se_aplica' && (
                  <div><strong>Ciclo Menstrual Regular:</strong> {prontuario.ciclo_menstrual_regular === 'sim' ? 'Sim' : 'Não'}</div>
                )}
                {prontuario.usa_anticoncepcional && (
                  <div><strong>Anticoncepcional:</strong> {prontuario.usa_anticoncepcional}</div>
                )}
                {prontuario.dificuldade_engravidar && prontuario.dificuldade_engravidar !== 'nao_se_aplica' && (
                  <div><strong>Dificuldade para engravidar:</strong> {prontuario.dificuldade_engravidar === 'sim' ? 'Sim' : 'Não'}</div>
                )}
                {prontuario.menopausa_status && prontuario.menopausa_status !== 'nao_se_aplica' && (
                  <div><strong>Menopausa:</strong> {prontuario.menopausa_status === 'sim' ? 'Sim' : prontuario.menopausa_status === 'perimenopausa' ? 'Perimenopausa' : 'Não'}</div>
                )}
                {prontuario.sintomas_menopausa && (
                  <div><strong>Sintomas:</strong> {prontuario.sintomas_menopausa}</div>
                )}
                {prontuario.alteracoes_sexuais_masculinas && (
                  <div><strong>Alterações (masculino):</strong> {prontuario.alteracoes_sexuais_masculinas}</div>
                )}
                {prontuario.historico_reprodutivo && (
                  <div><strong>Observações:</strong> {prontuario.historico_reprodutivo}</div>
                )}
                {!prontuario.ciclo_menstrual_regular && !prontuario.usa_anticoncepcional && !prontuario.historico_reprodutivo && (
                  <p className={styles.semDados}>Sem informações registradas</p>
                )}
              </div>
            </div>

            {/* Objetivo do Paciente */}
            <div className={styles.subsection}>
              <div className={styles.subsectionHeader}>
                <h4>🎯 Objetivo do Paciente</h4>
                {!modoVisualizacao && (
                  <button 
                    className={styles.btnEditar}
                    onClick={abrirModalObjetivo}
                    title="Editar Objetivo"
                  >
                    ✏️ Editar
                  </button>
                )}
              </div>
              {prontuario.objetivo_paciente ? (
                <div className={styles.infoBox}>{prontuario.objetivo_paciente}</div>
              ) : (
                <p className={styles.semDados}>Objetivo não definido</p>
              )}
            </div>

            {/* Última Evolução */}
            {ultima_evolucao && (
              <div className={styles.subsection}>
                <h4>📊 Última Evolução ({formatarData(ultima_evolucao.data_evolucao)})</h4>
                <div className={styles.sinaisVitais}>
                  {ultima_evolucao.peso && <div><strong>Peso:</strong> {ultima_evolucao.peso} kg</div>}
                  {ultima_evolucao.altura && <div><strong>Altura:</strong> {ultima_evolucao.altura} m</div>}
                  {ultima_evolucao.imc && <div><strong>IMC:</strong> {ultima_evolucao.imc}</div>}
                  {ultima_evolucao.pressao_arterial && <div><strong>PA:</strong> {ultima_evolucao.pressao_arterial}</div>}
                  {ultima_evolucao.frequencia_cardiaca && <div><strong>FC:</strong> {ultima_evolucao.frequencia_cardiaca} bpm</div>}
                  {ultima_evolucao.temperatura && <div><strong>Temp:</strong> {ultima_evolucao.temperatura}°C</div>}
                  {ultima_evolucao.saturacao_oxigenio && <div><strong>SpO2:</strong> {ultima_evolucao.saturacao_oxigenio}%</div>}
                </div>
                {ultima_evolucao.queixa && (
                  <div className={styles.infoBox}>
                    <strong>Queixa:</strong> {ultima_evolucao.queixa}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* COLUNA DIREITA - Nova Evolução */}
        {!modoVisualizacao && (
          <div className={styles.colunaDireita}>
            <div className={styles.section}>
              <h3>✍️ Registrar Nova Evolução</h3>

              {/* Sinais Vitais */}
            <div className={styles.formGroup}>
              <h4>📊 Sinais Vitais</h4>
              <div className={styles.formGrid}>
                <div>
                  <label>Peso (kg)</label>
                  <input
                    type="number"
                    name="peso"
                    step="0.1"
                    value={novaEvolucao.peso}
                    onChange={handleInputChange}
                    className={styles.input}
                  />
                </div>
                <div>
                  <label>Altura (m)</label>
                  <input
                    type="number"
                    name="altura"
                    step="0.01"
                    value={novaEvolucao.altura}
                    onChange={handleInputChange}
                    className={styles.input}
                  />
                </div>
                {calcularIMC() && (
                  <div>
                    <label>IMC (calculado)</label>
                    <input
                      type="text"
                      value={calcularIMC()}
                      disabled
                      className={styles.inputDisabled}
                    />
                  </div>
                )}
                <div>
                  <label>Pressão Arterial</label>
                  <input
                    type="text"
                    name="pressao_arterial"
                    placeholder="120/80"
                    value={novaEvolucao.pressao_arterial}
                    onChange={handleInputChange}
                    className={styles.input}
                  />
                </div>
                <div>
                  <label>FC (bpm)</label>
                  <input
                    type="number"
                    name="frequencia_cardiaca"
                    value={novaEvolucao.frequencia_cardiaca}
                    onChange={handleInputChange}
                    className={styles.input}
                  />
                </div>
                <div>
                  <label>Temperatura (°C)</label>
                  <input
                    type="number"
                    name="temperatura"
                    step="0.1"
                    value={novaEvolucao.temperatura}
                    onChange={handleInputChange}
                    className={styles.input}
                  />
                </div>
                <div>
                  <label>SpO2 (%)</label>
                  <input
                    type="number"
                    name="saturacao_oxigenio"
                    value={novaEvolucao.saturacao_oxigenio}
                    onChange={handleInputChange}
                    className={styles.input}
                  />
                </div>
              </div>
            </div>

            {/* Sintomas Atuais */}
            <div className={styles.formGroup}>
              <h4>😷 Sintomas Atuais</h4>
              <textarea
                name="sintomas_atuais"
                value={novaEvolucao.sintomas_atuais}
                onChange={handleInputChange}
                className={styles.textarea}
                placeholder="Ganho/perda de peso, apetite, cansaço, sono, alterações intestinais, sede, etc."
                rows="4"
              />
            </div>

            {/* Medicamentos Atuais */}
            <div className={styles.formGroup}>
              <h4>💊 Medicamentos em Uso Atualmente</h4>
              <textarea
                name="medicamentos_atuais_consulta"
                value={novaEvolucao.medicamentos_atuais_consulta}
                onChange={handleInputChange}
                className={styles.textarea}
                placeholder="Liste os medicamentos que o paciente está usando no momento..."
                rows="3"
              />
            </div>

            {/* Estilo de Vida */}
            <div className={styles.formGroup}>
              <h4>🏃 Estilo de Vida Atual</h4>
              <textarea
                name="estilo_vida_atual"
                value={novaEvolucao.estilo_vida_atual}
                onChange={handleInputChange}
                className={styles.textarea}
                placeholder="Alimentação, exercícios físicos, sono, nível de estresse, etc."
                rows="4"
              />
            </div>

            {/* Exames Laboratoriais */}
            <div className={styles.formGroup}>
              <h4>🔬 Exames Laboratoriais Recentes</h4>
              <div className={styles.formGrid}>
                <div>
                  <label>TSH / T4 Livre / T3</label>
                  <input
                    type="text"
                    name="exames_lab_tsh_t4_t3"
                    value={novaEvolucao.exames_lab_tsh_t4_t3}
                    onChange={handleInputChange}
                    className={styles.input}
                    placeholder="Resultados dos exames de tireoide"
                  />
                </div>
                <div>
                  <label>Hemograma</label>
                  <input
                    type="text"
                    name="exames_lab_hemograma"
                    value={novaEvolucao.exames_lab_hemograma}
                    onChange={handleInputChange}
                    className={styles.input}
                    placeholder="Principais valores"
                  />
                </div>
                <div>
                  <label>Glicemia / HbA1c</label>
                  <input
                    type="text"
                    name="exames_lab_glicemia_hba1c"
                    value={novaEvolucao.exames_lab_glicemia_hba1c}
                    onChange={handleInputChange}
                    className={styles.input}
                    placeholder="Ex: Glicemia 95 mg/dL, HbA1c 5.4%"
                  />
                </div>
                <div>
                  <label>Insulina / HOMA-IR</label>
                  <input
                    type="text"
                    name="exames_lab_insulina_homa"
                    value={novaEvolucao.exames_lab_insulina_homa}
                    onChange={handleInputChange}
                    className={styles.input}
                    placeholder="Insulina e índice HOMA-IR"
                  />
                </div>
                <div>
                  <label>Vitamina D</label>
                  <input
                    type="text"
                    name="exames_lab_vitamina_d"
                    value={novaEvolucao.exames_lab_vitamina_d}
                    onChange={handleInputChange}
                    className={styles.input}
                    placeholder="ng/mL"
                  />
                </div>
                <div>
                  <label>Cortisol</label>
                  <input
                    type="text"
                    name="exames_lab_cortisol"
                    value={novaEvolucao.exames_lab_cortisol}
                    onChange={handleInputChange}
                    className={styles.input}
                    placeholder="mcg/dL"
                  />
                </div>
                <div>
                  <label>Prolactina</label>
                  <input
                    type="text"
                    name="exames_lab_prolactina"
                    value={novaEvolucao.exames_lab_prolactina}
                    onChange={handleInputChange}
                    className={styles.input}
                    placeholder="ng/mL"
                  />
                </div>
                <div>
                  <label>Perfil Lipídico</label>
                  <input
                    type="text"
                    name="exames_lab_perfil_lipidico"
                    value={novaEvolucao.exames_lab_perfil_lipidico}
                    onChange={handleInputChange}
                    className={styles.input}
                    placeholder="Colesterol, HDL, LDL, Triglicerídeos"
                  />
                </div>
                <div>
                  <label>Função Hepática</label>
                  <input
                    type="text"
                    name="exames_lab_funcao_hepatica"
                    value={novaEvolucao.exames_lab_funcao_hepatica}
                    onChange={handleInputChange}
                    className={styles.input}
                    placeholder="TGO, TGP, GGT"
                  />
                </div>
                <div>
                  <label>Função Renal</label>
                  <input
                    type="text"
                    name="exames_lab_funcao_renal"
                    value={novaEvolucao.exames_lab_funcao_renal}
                    onChange={handleInputChange}
                    className={styles.input}
                    placeholder="Ureia, Creatinina"
                  />
                </div>
                <div>
                  <label>Ultrassonografia</label>
                  <input
                    type="text"
                    name="exames_lab_ultrassom"
                    value={novaEvolucao.exames_lab_ultrassom}
                    onChange={handleInputChange}
                    className={styles.input}
                    placeholder="Tireoide, abdômen, transvaginal"
                  />
                </div>
                <div>
                  <label>Outros Exames</label>
                  <input
                    type="text"
                    name="exames_lab_outros"
                    value={novaEvolucao.exames_lab_outros}
                    onChange={handleInputChange}
                    className={styles.input}
                    placeholder="Outros exames relevantes"
                  />
                </div>
              </div>
            </div>

            {/* Avaliação Clínica */}
            <div className={styles.formGroup}>
              <h4>🩺 Avaliação Clínica</h4>
              
              <label>Queixa Principal *</label>
              <textarea
                name="queixa"
                value={novaEvolucao.queixa}
                onChange={handleInputChange}
                className={styles.textarea}
                rows="3"
                required
              />

              <label>Exame Físico</label>
              <textarea
                name="exame_fisico"
                value={novaEvolucao.exame_fisico}
                onChange={handleInputChange}
                className={styles.textarea}
                rows="4"
              />

              <label>Hipótese Diagnóstica</label>
              <textarea
                name="hipotese_diagnostica"
                value={novaEvolucao.hipotese_diagnostica}
                onChange={handleInputChange}
                className={styles.textarea}
                rows="3"
              />

              <label>Conduta</label>
              <textarea
                name="conduta"
                value={novaEvolucao.conduta}
                onChange={handleInputChange}
                className={styles.textarea}
                rows="4"
              />

              <label>Plano Terapêutico</label>
              <textarea
                name="plano_terapeutico"
                value={novaEvolucao.plano_terapeutico}
                onChange={handleInputChange}
                className={styles.textarea}
                placeholder="Objetivos do tratamento, metas (peso, glicemia, TSH, lipídios), frequência de retorno, orientações especiais..."
                rows="4"
              />

              <label>Observações</label>
              <textarea
                name="observacoes"
                value={novaEvolucao.observacoes}
                onChange={handleInputChange}
                className={styles.textarea}
                rows="3"
              />
            </div>

            {!modoVisualizacao && (
              <button
                className={styles.btnSalvar}
                onClick={registrarEvolucao}
                disabled={loading || !novaEvolucao.queixa}
              >
                💾 Salvar Evolução
              </button>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Modal de Histórico */}
      {showHistorico && (
        <div className={styles.modal} onClick={() => setShowHistorico(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>📜 Histórico de Evoluções</h3>
              <button 
                className={styles.btnFechar}
                onClick={() => setShowHistorico(false)}
              >
                ✕
              </button>
            </div>
            <div className={styles.modalBody}>
              {evolucoes.length === 0 ? (
                <p className={styles.semDados}>Nenhuma evolução registrada</p>
              ) : (
                evolucoes.map((evolucao, index) => (
                  <div key={evolucao.id} className={styles.evolucaoItem}>
                    <div className={styles.evolucaoHeader}>
                      <strong>Evolução #{evolucoes.length - index}</strong>
                      <span>{formatarDataHora(evolucao.data_evolucao)}</span>
                    </div>
                    <div className={styles.evolucaoContent}>
                      
                      {/* Sinais Vitais */}
                      <div className={styles.evolucaoSecao}>
                        <h4>🩺 Sinais Vitais</h4>
                        <div className={styles.evolucaoGrid}>
                          {evolucao.peso && <p><strong>Peso:</strong> {evolucao.peso} kg</p>}
                          {evolucao.altura && <p><strong>Altura:</strong> {evolucao.altura} m</p>}
                          {evolucao.imc && <p><strong>IMC:</strong> {evolucao.imc}</p>}
                          {evolucao.pressao_arterial && <p><strong>PA:</strong> {evolucao.pressao_arterial}</p>}
                          {evolucao.frequencia_cardiaca && <p><strong>FC:</strong> {evolucao.frequencia_cardiaca} bpm</p>}
                          {evolucao.temperatura && <p><strong>Temp:</strong> {evolucao.temperatura}°C</p>}
                          {evolucao.frequencia_respiratoria && <p><strong>FR:</strong> {evolucao.frequencia_respiratoria} irpm</p>}
                          {evolucao.saturacao_oxigenio && <p><strong>SpO2:</strong> {evolucao.saturacao_oxigenio}%</p>}
                          {evolucao.glicemia && <p><strong>Glicemia:</strong> {evolucao.glicemia} mg/dL</p>}
                          {evolucao.circunferencia_abdominal && <p><strong>Circunf. Abdominal:</strong> {evolucao.circunferencia_abdominal} cm</p>}
                        </div>
                      </div>

                      {/* Sintomas e Estado Atual */}
                      {(evolucao.sintomas_atuais || evolucao.medicamentos_atuais_consulta || evolucao.estilo_vida_atual) && (
                        <div className={styles.evolucaoSecao}>
                          <h4>🔍 Sintomas e Estado Atual</h4>
                          {evolucao.sintomas_atuais && (
                            <p><strong>Sintomas Atuais:</strong> {evolucao.sintomas_atuais}</p>
                          )}
                          {evolucao.medicamentos_atuais_consulta && (
                            <p><strong>Medicamentos em Uso:</strong> {evolucao.medicamentos_atuais_consulta}</p>
                          )}
                          {evolucao.estilo_vida_atual && (
                            <p><strong>Estilo de Vida:</strong> {evolucao.estilo_vida_atual}</p>
                          )}
                        </div>
                      )}

                      {/* Exames Laboratoriais Recentes */}
                      {(evolucao.exames_lab_tsh_t4_t3 || evolucao.exames_lab_hemograma || evolucao.exames_lab_glicemia_hba1c || 
                        evolucao.exames_lab_insulina_homa || evolucao.exames_lab_vitamina_d || evolucao.exames_lab_cortisol ||
                        evolucao.exames_lab_prolactina || evolucao.exames_lab_perfil_lipidico || evolucao.exames_lab_funcao_hepatica ||
                        evolucao.exames_lab_funcao_renal || evolucao.exames_lab_ultrassom || evolucao.exames_lab_outros) && (
                        <div className={styles.evolucaoSecao}>
                          <h4>🧪 Exames Laboratoriais Recentes</h4>
                          <div className={styles.evolucaoGrid}>
                            {evolucao.exames_lab_tsh_t4_t3 && <p><strong>TSH/T4/T3:</strong> {evolucao.exames_lab_tsh_t4_t3}</p>}
                            {evolucao.exames_lab_hemograma && <p><strong>Hemograma:</strong> {evolucao.exames_lab_hemograma}</p>}
                            {evolucao.exames_lab_glicemia_hba1c && <p><strong>Glicemia/HbA1c:</strong> {evolucao.exames_lab_glicemia_hba1c}</p>}
                            {evolucao.exames_lab_insulina_homa && <p><strong>Insulina/HOMA-IR:</strong> {evolucao.exames_lab_insulina_homa}</p>}
                            {evolucao.exames_lab_vitamina_d && <p><strong>Vitamina D:</strong> {evolucao.exames_lab_vitamina_d}</p>}
                            {evolucao.exames_lab_cortisol && <p><strong>Cortisol:</strong> {evolucao.exames_lab_cortisol}</p>}
                            {evolucao.exames_lab_prolactina && <p><strong>Prolactina:</strong> {evolucao.exames_lab_prolactina}</p>}
                            {evolucao.exames_lab_perfil_lipidico && <p><strong>Perfil Lipídico:</strong> {evolucao.exames_lab_perfil_lipidico}</p>}
                            {evolucao.exames_lab_funcao_hepatica && <p><strong>Função Hepática:</strong> {evolucao.exames_lab_funcao_hepatica}</p>}
                            {evolucao.exames_lab_funcao_renal && <p><strong>Função Renal:</strong> {evolucao.exames_lab_funcao_renal}</p>}
                            {evolucao.exames_lab_ultrassom && <p><strong>Ultrassonografia:</strong> {evolucao.exames_lab_ultrassom}</p>}
                            {evolucao.exames_lab_outros && <p><strong>Outros Exames:</strong> {evolucao.exames_lab_outros}</p>}
                          </div>
                        </div>
                      )}

                      {/* Avaliação Clínica */}
                      <div className={styles.evolucaoSecao}>
                        <h4>📋 Avaliação Clínica</h4>
                        {evolucao.queixa && (
                          <p><strong>Queixa Principal:</strong> {evolucao.queixa}</p>
                        )}
                        {evolucao.exame_fisico && (
                          <p><strong>Exame Físico:</strong> {evolucao.exame_fisico}</p>
                        )}
                      </div>

                      {/* Diagnóstico e Conduta */}
                      <div className={styles.evolucaoSecao}>
                        <h4>💊 Diagnóstico e Conduta</h4>
                        {evolucao.hipotese_diagnostica && (
                          <p><strong>Hipótese Diagnóstica:</strong> {evolucao.hipotese_diagnostica}</p>
                        )}
                        {evolucao.diagnostico_final && (
                          <p><strong>Diagnóstico Final:</strong> {evolucao.diagnostico_final}</p>
                        )}
                        {evolucao.conduta && (
                          <p><strong>Conduta:</strong> {evolucao.conduta}</p>
                        )}
                        {evolucao.plano_terapeutico && (
                          <p><strong>Plano Terapêutico:</strong> {evolucao.plano_terapeutico}</p>
                        )}
                        {evolucao.orientacoes && (
                          <p><strong>Orientações:</strong> {evolucao.orientacoes}</p>
                        )}
                        {evolucao.exames_solicitados && (
                          <p><strong>Exames Solicitados:</strong> {evolucao.exames_solicitados}</p>
                        )}
                        {evolucao.procedimentos_realizados && (
                          <p><strong>Procedimentos:</strong> {evolucao.procedimentos_realizados}</p>
                        )}
                      </div>

                      {/* Observações */}
                      {evolucao.observacoes && (
                        <div className={styles.evolucaoSecao}>
                          <h4>📝 Observações</h4>
                          <p>{evolucao.observacoes}</p>
                        </div>
                      )}

                      {/* Informações do Atendimento */}
                      <div className={styles.evolucaoFooter}>
                        {evolucao.medico_nome && (
                          <small>👨‍⚕️ {evolucao.medico_nome} - CRM: {evolucao.medico_crm}</small>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar Alergias */}
      {showModalAlergias && (
        <div className={styles.modalOverlay} onClick={() => setShowModalAlergias(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>⚠️ Editar Alergias</h3>
              <button 
                className={styles.btnFechar}
                onClick={() => setShowModalAlergias(false)}
              >
                ✖️
              </button>
            </div>
            <div className={styles.modalBody}>
              <textarea
                className={styles.textareaModal}
                value={tempAlergias}
                onChange={(e) => setTempAlergias(e.target.value)}
                placeholder="Descreva as alergias do paciente (medicamentos, alimentos, etc.)..."
                rows={6}
              />
            </div>
            <div className={styles.modalFooter}>
              <button 
                className={styles.btnCancelar}
                onClick={() => setShowModalAlergias(false)}
              >
                Cancelar
              </button>
              <button 
                className={styles.btnSalvar}
                onClick={salvarAlergias}
                disabled={loading}
              >
                💾 Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar Medicamentos */}
      {showModalMedicamentos && (
        <div className={styles.modalOverlay} onClick={() => setShowModalMedicamentos(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>💊 Editar Medicamentos de Uso Contínuo</h3>
              <button 
                className={styles.btnFechar}
                onClick={() => setShowModalMedicamentos(false)}
              >
                ✖️
              </button>
            </div>
            <div className={styles.modalBody}>
              <textarea
                className={styles.textareaModal}
                value={tempMedicamentos}
                onChange={(e) => setTempMedicamentos(e.target.value)}
                placeholder="Liste os medicamentos de uso contínuo do paciente (nome, dosagem, frequência)..."
                rows={6}
              />
            </div>
            <div className={styles.modalFooter}>
              <button 
                className={styles.btnCancelar}
                onClick={() => setShowModalMedicamentos(false)}
              >
                Cancelar
              </button>
              <button 
                className={styles.btnSalvar}
                onClick={salvarMedicamentos}
                disabled={loading}
              >
                💾 Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar Histórico Familiar */}
      {showModalHistoricoFamiliar && (
        <div className={styles.modalOverlay} onClick={() => setShowModalHistoricoFamiliar(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>👨‍👩‍👧‍👦 Editar Histórico Familiar</h3>
              <button 
                className={styles.btnFechar}
                onClick={() => setShowModalHistoricoFamiliar(false)}
              >
                ✖️
              </button>
            </div>
            <div className={styles.modalBody}>
              <textarea
                className={styles.textareaModal}
                value={tempHistoricoFamiliar}
                onChange={(e) => setTempHistoricoFamiliar(e.target.value)}
                placeholder="Descreva o histórico familiar (doenças hereditárias, antecedentes familiares)..."
                rows={6}
              />
            </div>
            <div className={styles.modalFooter}>
              <button 
                className={styles.btnCancelar}
                onClick={() => setShowModalHistoricoFamiliar(false)}
              >
                Cancelar
              </button>
              <button 
                className={styles.btnSalvar}
                onClick={salvarHistoricoFamiliar}
                disabled={loading}
              >
                💾 Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar Cirurgias Prévias */}
      {showModalCirurgias && (
        <div className={styles.modalOverlay} onClick={() => setShowModalCirurgias(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>🏥 Editar Cirurgias Prévias</h3>
              <button 
                className={styles.btnFechar}
                onClick={() => setShowModalCirurgias(false)}
              >
                ✖️
              </button>
            </div>
            <div className={styles.modalBody}>
              <textarea
                className={styles.textareaModal}
                value={tempCirurgias}
                onChange={(e) => setTempCirurgias(e.target.value)}
                placeholder="Liste as cirurgias prévias do paciente (procedimento, data aproximada)..."
                rows={6}
              />
            </div>
            <div className={styles.modalFooter}>
              <button 
                className={styles.btnCancelar}
                onClick={() => setShowModalCirurgias(false)}
              >
                Cancelar
              </button>
              <button 
                className={styles.btnSalvar}
                onClick={salvarCirurgias}
                disabled={loading}
              >
                💾 Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar Histórico Reprodutivo */}
      {showModalHistoricoReprodutivo && (
        <div className={styles.modalOverlay} onClick={() => setShowModalHistoricoReprodutivo(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>🔬 Editar Histórico Reprodutivo/Hormonal</h3>
              <button 
                className={styles.btnFechar}
                onClick={() => setShowModalHistoricoReprodutivo(false)}
              >
                ✖️
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label>Ciclo menstrual regular?</label>
                <select
                  className={styles.input}
                  value={tempHistoricoReprodutivo.ciclo_menstrual_regular}
                  onChange={(e) => setTempHistoricoReprodutivo({
                    ...tempHistoricoReprodutivo,
                    ciclo_menstrual_regular: e.target.value
                  })}
                >
                  <option value="nao_se_aplica">Não se aplica</option>
                  <option value="sim">Sim</option>
                  <option value="nao">Não</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label>Usa anticoncepcional? Qual?</label>
                <input
                  type="text"
                  className={styles.input}
                  value={tempHistoricoReprodutivo.usa_anticoncepcional}
                  onChange={(e) => setTempHistoricoReprodutivo({
                    ...tempHistoricoReprodutivo,
                    usa_anticoncepcional: e.target.value
                  })}
                  placeholder="Ex: Pílula combinada, DIU..."
                />
              </div>

              <div className={styles.formGroup}>
                <label>Dificuldade para engravidar?</label>
                <select
                  className={styles.input}
                  value={tempHistoricoReprodutivo.dificuldade_engravidar}
                  onChange={(e) => setTempHistoricoReprodutivo({
                    ...tempHistoricoReprodutivo,
                    dificuldade_engravidar: e.target.value
                  })}
                >
                  <option value="nao_se_aplica">Não se aplica</option>
                  <option value="sim">Sim</option>
                  <option value="nao">Não</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label>Status da menopausa</label>
                <select
                  className={styles.input}
                  value={tempHistoricoReprodutivo.menopausa_status}
                  onChange={(e) => setTempHistoricoReprodutivo({
                    ...tempHistoricoReprodutivo,
                    menopausa_status: e.target.value
                  })}
                >
                  <option value="nao_se_aplica">Não se aplica</option>
                  <option value="nao">Não</option>
                  <option value="perimenopausa">Perimenopausa</option>
                  <option value="sim">Sim (menopausa estabelecida)</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label>Sintomas da menopausa (calorões, insônia, etc.)</label>
                <textarea
                  className={styles.textareaModal}
                  value={tempHistoricoReprodutivo.sintomas_menopausa}
                  onChange={(e) => setTempHistoricoReprodutivo({
                    ...tempHistoricoReprodutivo,
                    sintomas_menopausa: e.target.value
                  })}
                  placeholder="Descreva os sintomas..."
                  rows={3}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Alterações sexuais masculinas (libido, energia, força)</label>
                <textarea
                  className={styles.textareaModal}
                  value={tempHistoricoReprodutivo.alteracoes_sexuais_masculinas}
                  onChange={(e) => setTempHistoricoReprodutivo({
                    ...tempHistoricoReprodutivo,
                    alteracoes_sexuais_masculinas: e.target.value
                  })}
                  placeholder="Descreva alterações se aplicável..."
                  rows={3}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Observações gerais do histórico reprodutivo</label>
                <textarea
                  className={styles.textareaModal}
                  value={tempHistoricoReprodutivo.historico_reprodutivo}
                  onChange={(e) => setTempHistoricoReprodutivo({
                    ...tempHistoricoReprodutivo,
                    historico_reprodutivo: e.target.value
                  })}
                  placeholder="Outras informações relevantes..."
                  rows={4}
                />
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button 
                className={styles.btnCancelar}
                onClick={() => setShowModalHistoricoReprodutivo(false)}
              >
                Cancelar
              </button>
              <button 
                className={styles.btnSalvar}
                onClick={salvarHistoricoReprodutivo}
                disabled={loading}
              >
                💾 Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar Objetivo do Paciente */}
      {showModalObjetivo && (
        <div className={styles.modalOverlay} onClick={() => setShowModalObjetivo(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>🎯 Editar Objetivo do Paciente</h3>
              <button 
                className={styles.btnFechar}
                onClick={() => setShowModalObjetivo(false)}
              >
                ✖️
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label>Principais objetivos do paciente:</label>
                <div style={{ marginBottom: '10px' }}>
                  <small>Ex: Emagrecimento, ganho de massa muscular, regular hormônios/tireoide, controlar diabetes, regular ciclo menstrual, melhorar fertilidade, etc.</small>
                </div>
                <textarea
                  className={styles.textareaModal}
                  value={tempObjetivo}
                  onChange={(e) => setTempObjetivo(e.target.value)}
                  placeholder="Descreva os objetivos principais do tratamento..."
                  rows={6}
                />
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button 
                className={styles.btnCancelar}
                onClick={() => setShowModalObjetivo(false)}
              >
                Cancelar
              </button>
              <button 
                className={styles.btnSalvar}
                onClick={salvarObjetivo}
                disabled={loading}
              >
                💾 Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Atendimento;
